import { Dialog, DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  EventEmitter,
  HostBinding,
  inject,
  input,
  OnInit,
  Output,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '@core/authentication/auth.service';
import {
  ApplicationService,
  AttachmentService,
  ClientService,
  ContactService,
  OcrService,
} from '@core/services';
import { environment } from '@environments/environment';
import {
  OiqFormAddressComponent,
  OiqFormCheckboxComponent,
  OiqFormDialogComponentInterface,
  OiqFormDobComponent,
  OiqFormDropdownComponent,
  OiqFormInputComponent,
  OiqFormSsnComponent,
  OiqFormTextComponent,
  OiqFormValueComponent,
} from '@oiq-ui-sdk/form';
import { OiqUiLoader, OiqUiPanelComponent } from '@oiq-ui-sdk/ui';
import { OcrMatchDialogService } from '@v2/modules/applications/add-application/components/ocr-match-dialog/ocr-match-dialog.component';
import { OiqApplicationEventService } from '@v2/modules/applications/components/event-dialog/event-dialog.component';
import { OiqClientSearchDialog } from '@v2/modules/clients/components/client-search/client-search-dialog.component';
import { OiqIsoSearchComponent } from '@v2/modules/isos/components/iso-search/iso-search.component';
import { OiqIsoSalesRepSelectComponent } from '@v2/modules/isos/components/sales-rep-select/sales-rep-select.component';
import { OiqAttachmentsUploadComponent } from '@v2shared/components/attachments/attachments-upload.component';
import { OiqAttachmentsUploadDialogResult } from '@v2shared/components/attachments/attachments-upload.models';
import { OiqAttachmentsUploadStore } from '@v2shared/components/attachments/attachments-upload.store';
import { OiqImagePreviewZoomComponent } from '@v2shared/components/image-preview-zoom/image-preview-zoom.component';
import { OiqSsnAlertComponent } from '@v2shared/components/ssn-alert/ssn-alert.component';
import {
  OIQ_SSN_ALERT_STORE,
  OIQ_SSN_ALERT_STORE_PROVIDER,
} from '@v2shared/components/ssn-alert/ssn-alert.store';
import { distinctUntilChanged, map, switchMap } from 'rxjs';
import { OiqApplicationAddForm } from './add-application.form';
import { OiqApplicationAddStore } from './add-application.store';

@Component({
  selector: 'oiq-application-add',
  host: {
    class: 'oiq-application-add',
  },
  templateUrl: './add-application.component.html',
  styleUrl: './add-application.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    ReactiveFormsModule,
    OiqUiPanelComponent,
    OiqFormCheckboxComponent,
    OiqFormSsnComponent,
    OiqFormDobComponent,
    OiqFormAddressComponent,
    OiqIsoSearchComponent,
    AsyncPipe,
    OiqFormDropdownComponent,
    OiqFormTextComponent,
    OiqFormValueComponent,
    OiqFormInputComponent,
    OiqAttachmentsUploadComponent,
    OiqImagePreviewZoomComponent,
    OiqUiLoader,
    OiqIsoSalesRepSelectComponent,
    OiqSsnAlertComponent,
  ],
  providers: [OIQ_SSN_ALERT_STORE_PROVIDER],
})
export class OiqApplicationAddComponent implements OiqFormDialogComponentInterface, OnInit {
  @HostBinding('class.__in_dialog') get classInDialog() {
    return this.dialogRef;
  }
  @Output() event = new EventEmitter<{ action: string; data?: any }>();
  mode = input<'upload' | 'application'>('application');
  initialData = input<any>(null);
  cdr = inject(ChangeDetectorRef);
  dialog = inject(Dialog);
  dialogRef = inject<DialogRef<any, OiqApplicationAddComponent>>(DialogRef, {
    optional: true,
  });
  public dialogData: any = inject(DIALOG_DATA, { optional: true });
  private _destroyRef = inject(DestroyRef);
  private _router = inject(Router);
  private _attachmentsService = inject(AttachmentService);
  private _clientsService = inject(ClientService);
  private _applicationsService = inject(ApplicationService);
  private _contactsService = inject(ContactService);
  private _ocrService = inject(OcrService);
  private _eventDialogService = inject(OiqApplicationEventService);
  private _auth = inject(AuthService);
  private _clientOcrMatchDialog = inject(OcrMatchDialogService);
  private _clientSearchDialog = inject(OiqClientSearchDialog);
  ssnAlert = inject(OIQ_SSN_ALERT_STORE);

  store = inject(OiqApplicationAddStore);
  attachments = inject(OiqAttachmentsUploadStore);
  formRef = new OiqApplicationAddForm(
    this._applicationsService,
    this._contactsService,
    this._attachmentsService,
    this._ocrService,
    this._clientsService
  );
  whiteLabelId = toSignal(this.formRef.whiteLabelId$);
  showOperationsNotes = signal<boolean>(false);
  showChildren = signal<boolean>(false);
  filesCount = computed(() =>
    this.showChildren() ? this.attachments.list().length : this.attachments.parentsCount()
  );
  public uploadUrl = `${environment.servicesUrl}/attachments/upload?type=application`;

  constructor() {
    this.store.tenantId = this._auth.getUser().tenantId;
    this.attachments.tenantId = this._auth.getUser().tenantId;
    effect(() => {
      const _attachmentIds = (this.attachments.list() || [])
        .filter(item => !item.parentId)
        .map(item => item.id);
      const _ocrIds = Object.values(this.attachments.ocrProcessed() || {}).map(item => item.id);
      this.formRef.patchValue({
        ocrIds: _ocrIds,
        attachmentIds: _attachmentIds,
      });
    });

    effect(() => {
      const _initialData = this.initialData();
      if (!_initialData) return;
      this.formRef.patchValue(_initialData);
    });

    this.formRef.clientId$
      .pipe(takeUntilDestroyed(this._destroyRef), distinctUntilChanged())
      .subscribe(clientId => {
        this.store.clientId$.next(clientId);
      });

    this.formRef.isoId$
      .pipe(takeUntilDestroyed(this._destroyRef), distinctUntilChanged())
      .subscribe(isoId => {
        this.store.isoId$.next(isoId);
      });

    this.store.clientData$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .pipe(distinctUntilChanged())
      .subscribe(client => {
        this.formRef.client = client;
      });

    this.store.tenantSettings$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(_settings => {
      this.formRef.features = _settings?.features || {};
      this.formRef.excludeFromCreditCheck = !!_settings.excludeFromCreditCheck;
    });

    this.store.whiteLabels$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(_list => {
      this.formRef.whiteLabels = _list || [];
    });

    // flag an owner SSN that already exists on another client as soon as it is entered
    this.ssnAlert.watch(
      this.formRef.creditCheckRequests.valueChanges.pipe(map(() => this.formRef.ssnLookup)),
      { debounce: 500 }
    );
  }

  ngOnInit(): void {
    if (this.dialogRef) {
      this.dialogRef.componentRef.setInput('mode', this.dialogData?.mode || 'application');
      this.dialogRef.componentRef.setInput('initialData', this.dialogData?.initialData || null);
    }
  }

  handleAttachmentsEvents(event: { type: string; data?: any }) {
    switch (event.type) {
      case 'ocr':
        this.attachments
          .ocrRun(event.data.item)
          .pipe(
            switchMap(_ => {
              this.attachments.select(event.data.item, true, false);
              this.attachments.view(event.data.item);
              return this.attachments.ocrProcess();
            })
          )
          .subscribe({
            next: _result => {
              return this.showOcrMatchDialog({
                action: 'ocr',
                files: this.attachments.selectionList(),
                processResults: _result,
              });
            },
          });
        // this.showOcrUploadDialog();
        break;
    }
  }

  onCloseImagePreview(): void {
    this.attachments.viewing.set({});
  }

  private _declineApplication(client: any, application: any, funding: any, reason: string = '') {
    if (funding) {
      this.showDeclineDialog({
        applicationId: null,
        isoId: client?.isoId || client?.iso?.id || funding?.isoId || funding?.iso.id,
        declineReason: reason,
      });
      return;
    }

    this.showDeclineDialog({
      applicationId: application?.id,
      isoId: application?.isoId || client?.isoId,
      declineReason: reason || application?.declineReason,
    });
  }

  /**
   * Open client search dialog
   */
  openClientDialog(action: string): void {
    if (!this.formRef.whiteLabelId) {
      return;
    }
    this._clientSearchDialog
      .open({
        whiteLabels: this.formRef.whiteLabels,
        whiteLabelId: this.formRef.whiteLabelId,
        action: action,
        enableSelectClient: true,
        enableButtonOptions: false,
        enableMenuOptions: true,
        enableSelectFunding: false,
        enableSelectApplication: false,
      })
      .subscribe(result => {
        if (!result) return;

        this.formRef.fromSearchClient(result);

        if (result.action === 'decline') {
          this._declineApplication(result.client, result.application, result.funding);
          return;
        }
      });
  }

  /**
   * Open the OCR data confirmation dialog to review and edit extracted data.
   */
  showOcrMatchDialog(opts: OiqAttachmentsUploadDialogResult): void {
    this._clientOcrMatchDialog
      .open({
        files: opts.files,
        ocrResults: opts.processResults,
        whiteLabelId: this.formRef.whiteLabelId,
        whiteLabels: this.formRef.whiteLabels,
        excludeFromCreditCheck: this.formRef.excludeFromCreditCheck,
        enabledCreditCheck: this.formRef.hasCreditReport,
      })
      .closed.subscribe(result => {
        if (!result) return;

        this.formRef.fromMatchClient(result);

        if (result.action == 'decline') {
          this._declineApplication(result.client, result.application, result.funding);
          return;
        }

        if (this.dialogRef) {
          this.dialogRef.componentRef.setInput('mode', 'application');
        }
      });
  }

  onDecline() {
    this.formRef.markAllAsTouched();
    if (this.formRef.controls.isoId.invalid) {
      return;
    }

    this.showDeclineDialog({
      applicationId: null,
      isoId: this.formRef.value.isoId,
    });
  }

  /**
   * Decline application.
   */
  showDeclineDialog(opts: { applicationId?: number; isoId?: number; declineReason?: string }) {
    const data = this.formRef.value;
    this._eventDialogService
      .show({
        application: { id: opts.applicationId, isoId: opts.isoId, tenantId: this.store.tenantId },
        features: this.formRef.features,
        title: 'Decline Application',
        eventType: 'Decline',
        messageType: 'Application_Decline_Reason',
        enabledNotifications: 'yes',
        formValue: {
          message: opts.declineReason,
          isoRecipients: [],
          tenantRecipients: this.formRef.emailRecipients.controls.map(control => control.value),
        },
      })
      .subscribe(result => {
        if (!result) {
          return;
        }

        delete data.creditCheckRequests;

        data.event = {
          event: 'Decline',
          metadata: result,
        };

        this._applicationsService.createAndDecline(data).subscribe({
          next: (application: any) => {
            this.event.emit({ action: 'create', data: application });
            if (this.dialogRef) {
              this.dialogRef.close({ action: 'create', data: application });
            }
          },
          error: () => {},
        });
      });
  }

  onCreateApplication() {
    this.formRef.doCreate().subscribe({
      next: _application => {
        this.event.emit({ action: 'create', data: _application });
        if (this.dialogRef) {
          this.dialogRef.close({
            action: 'create',
            data: _application,
          });
        }
      },
      error: _reason => {
        console.error(
          'invalid fields',
          Object.entries(this.formRef.controls)
            .filter(item => item[1].invalid)
            .map(item => item[0]),
          _reason
        );
      },
    });
  }

  onClientEvent(event: { type: string; data?: any }) {
    switch (event.type) {
      case 'select':
        break;
      case 'clear':
        this.formRef.resetClientData();
        break;
    }
  }

  onCancel() {
    if (this.dialogRef) {
      this.dialogRef.close();
      return;
    }

    this._router.navigate(['/applications']);
  }

  onManually() {
    if (this.dialogRef) {
      this.dialogRef.componentRef.setInput('mode', 'application');
    }
  }

  onCreateUsingOcr() {
    this.attachments.ocrProcess().subscribe({
      next: _result => {
        this.showOcrMatchDialog({
          action: 'ocr',
          files: this.attachments.selectionList(),
          processResults: _result,
        });
      },
    });
  }
}
